/* eslint-disable */
import type { ColorFilterConfig } from '../core/types'
import { ColorFilterTransformer } from '../transformers/ColorFilterTransformer'

/**
 * CSS-only color filter fallback for browsers without Insertable Streams.
 *
 * Applies the CSS `filter` property directly on the publisher's
 * `<video>` DOM element. This is instant and zero-CPU, but has a key
 * limitation: **only the local display is affected**. Remote
 * participants see the original unfiltered video because the filter
 * is applied after WebRTC encoding.
 *
 * Temperature is approximated with `hue-rotate()` instead of the
 * canvas overlay used by {@link ColorFilterTransformer}.
 */
export class CSSFilterFallback {
  private element: HTMLElement | null = null
  private originalFilter = ''

  /**
   * Apply CSS color filters to the given DOM element.
   *
   * @param element - The `<video>` element (or its container).
   * @param config  - Color filter values.
   */
  apply (element: HTMLElement, config: ColorFilterConfig): void {
    this.element = element
    this.originalFilter = element.style.filter || ''
    element.style.filter = CSSFilterFallback.buildFilterString(config)
  }

  /**
   * Update the filter values on the currently targeted element.
   *
   * @param config - New color filter values.
   */
  update (config: ColorFilterConfig): void {
    if (this.element) {
      this.element.style.filter = CSSFilterFallback.buildFilterString(config)
    }
  }

  /** Remove the CSS filter and restore the element's original value. */
  remove (): void {
    if (this.element) {
      this.element.style.filter = this.originalFilter
      this.element = null
      this.originalFilter = ''
    }
  }

  /** Whether a CSS filter is currently applied. */
  isActive (): boolean {
    return this.element !== null
  }

  /**
   * Build a CSS `filter` property string including `hue-rotate` for
   * temperature approximation.
   *
   * @param config - Color filter values.
   * @returns CSS filter string ready for `element.style.filter`.
   */
  static buildFilterString (config: ColorFilterConfig): string {
    const c = ColorFilterTransformer.normalize(config)
    const parts: string[] = []

    if (c.brightness !== 1) parts.push(`brightness(${c.brightness})`)
    if (c.contrast !== 1) parts.push(`contrast(${c.contrast})`)
    if (c.saturation !== 1) parts.push(`saturate(${c.saturation})`)
    if (c.sepia > 0) parts.push(`sepia(${c.sepia})`)
    if (c.grayscale > 0) parts.push(`grayscale(${c.grayscale})`)

    if (c.temperature !== 0) {
      const deg = c.temperature * -30
      parts.push(`hue-rotate(${deg}deg)`)
    }

    return parts.join(' ')
  }
}
